'use client'

import { useConfig } from '@/context/ConfigProvider'
import { useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { Download, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState, type ReactNode } from 'react'

interface FilePreviewProps {
  fileId?: string
  blobUrl?: string
  blobType?: string
  url?: string
  name: string
  children: ReactNode
  className?: string
}

export default function FilePreview({ fileId, blobUrl, blobType, url, name, children, className }: FilePreviewProps) {
  const { apiUrl } = useConfig()
  const t = useTranslations('filePreview')
  const parentWidth = useChatStore(state => state.parentWidth)
  const siteId = usePersistStore(state => state.siteId)
  const token = usePersistStore(state => state.token)
  const guestId = usePersistStore(state => state.guestId)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const requestRef = useRef<AbortController | null>(null)
  const [preview, setPreview] = useState<{ url: string; type: string; name: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview.url)
  }, [preview])

  const openInParent = (blob?: Blob, error?: string): Promise<boolean> => {
    const hostIsNarrow = parentWidth != null && parentWidth <= 600
    if (typeof window === 'undefined' || window.parent === window || hostIsNarrow) return Promise.resolve(false)

    return new Promise(resolve => {
      const requestId = `${Date.now()}-${Math.random()}`
      const timeout = window.setTimeout(() => finish(false), 700)
      const onMessage = (event: MessageEvent) => {
        if (event.source === window.parent && event.data?.type === 'FILE_PREVIEW_OPENED' && event.data?.requestId === requestId) {
          finish(true)
        }
      }
      const finish = (opened: boolean) => {
        window.clearTimeout(timeout)
        window.removeEventListener('message', onMessage)
        resolve(opened)
      }

      window.addEventListener('message', onMessage)
      window.parent.postMessage({
        type: 'OPEN_FILE_PREVIEW', requestId, blob, error, name,
        labels: { close: t('close'), download: t('download') }
      }, '*')
    })
  }

  const open = async () => {
    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    dialogRef.current?.showModal()
    setError(false)
    setLoading(true)

    try {
      const localBlobUrl = blobUrl?.startsWith('blob:') || blobUrl?.startsWith('data:') ? blobUrl : null
      const requestUrl = new URL(
        localBlobUrl ||
          (fileId ? `${window.location.origin}/api/download/${encodeURIComponent(fileId)}` : url!)
      )
      const isFileService = !localBlobUrl && (fileId || requestUrl.origin === new URL(apiUrl).origin)
      if (isFileService && siteId) requestUrl.searchParams.set('site_id', siteId)
      const downloadToken = token === 'guest' ? guestId : token
      const response = await fetch(requestUrl, {
        headers: isFileService && downloadToken ? { Authorization: `Bearer ${downloadToken}` } : {},
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`File request failed: ${response.status}`)
      const fetchedBlob = await response.blob()
      const blob = localBlobUrl && !fetchedBlob.type && blobType
        ? new Blob([fetchedBlob], { type: blobType })
        : fetchedBlob
      if (controller.signal.aborted) return
      if (await openInParent(blob)) {
        dialogRef.current?.close()

        return
      }
      if (controller.signal.aborted) return
      setPreview({ url: URL.createObjectURL(blob), type: blob.type, name })
    } catch {
      if (!controller.signal.aborted) {
        if (await openInParent(undefined, t('error'))) {
          dialogRef.current?.close()
        } else {
          setError(true)
        }
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }

  const close = () => {
    requestRef.current?.abort()
    dialogRef.current?.close()
    setPreview(null)
    setLoading(false)
  }

  return (
    <>
      <button type='button' onClick={open} className={className}>
        {children}
      </button>
      <dialog ref={dialogRef} onClose={close} className='bg-background text-foreground m-auto max-h-[90vh] w-[min(90vw,48rem)] rounded-xl p-4 shadow-xl backdrop:bg-black/70'>
        <div className='mb-3 flex items-center justify-between gap-3'>
          <span className='min-w-0 truncate font-medium'>{name}</span>
          <button type='button' onClick={close} aria-label={t('close')}><X className='size-5' /></button>
        </div>
        {loading && <p>{t('loading')}</p>}
        {error && <p>{t('error')}</p>}
        {preview && (
          <>
            {preview.type.startsWith('image/') && <img src={preview.url} alt={name} className='max-h-[70vh] max-w-full object-contain' />}
            {preview.type === 'application/pdf' && <iframe src={preview.url} title={name} className='h-[70vh] w-full' />}
            {preview.type.startsWith('video/') && <video src={preview.url} controls className='max-h-[70vh] max-w-full' />}
            <a href={preview.url} download={preview.name} className='mt-3 inline-flex items-center gap-2 rounded border px-3 py-2'>
              <Download className='size-4' /> {t('download')}
            </a>
          </>
        )}
      </dialog>
    </>
  )
}
